import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import { useAuth } from '../contexts/AuthContext';
import { PAGE_ACCESS } from '../config/pageAccess';
import './Auth.css';
import './Admin.css';

// Display names for the grantable pages. This map only supplies labels — the
// list itself is derived from PAGE_ACCESS below, because maintaining a second
// hand-written list is what let seven pages go missing from this console.
const PAGE_LABELS = {
  '/athletic-fields': 'Athletic Fields Master Plan',
  '/carp-2026': 'CARP 2026',
  '/community-health-plan': 'Community Health Improvement Plan',
  '/2026-stm2-crypto-kiosk-faq': 'Cryptocurrency Kiosk FAQ',
  '/2026-stm2-reimburse-tmms-faq': 'Reimbursing Town Meeting Members FAQ',
  '/2026-stm2-psmod-faq': 'Pleasant Street Multifamily Overlay FAQ',
  '/dashboards': 'Dashboards (directory page)',
  '/dpw-strategic-plan': 'DPW Strategic Plan',
  '/ersc-recommendations': 'ERSC Recommendations',
  '/faq-platform-faq': 'FAQ Platform',
  '/governing-documents': 'Governing Documents',
  '/hazard-mitigation': 'Hazard Mitigation Plan',
  '/housing-production-plan': 'Housing Production Plan',
  '/memos': 'Memos',
  '/open-space': 'Open Space & Recreation',
  '/roadmap-2030': 'Roadmap 2030',
  '/school-staff': 'School Staff',
  '/town-budgets': 'Town Budgets',
  '/town-school-moa': 'Town–School Memorandum of Agreement',
  '/urban-forestry': 'Urban Forestry',
  '/vision-zero': 'Vision Zero Action Plan',
  '/voters': 'Voters',
};

// "/some-new-page" -> "Some New Page", so a page added to PAGE_ACCESS without a
// label here is still grantable rather than silently absent.
const labelFor = (path) =>
  PAGE_LABELS[path] ||
  path.replace(/^\//, '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Public pages that still carry a grantable capability. FAQ Platform is public
// to read, but its Submit tab — and the upload API behind it — require this
// permission row, so it must stay grantable even though it is not a private
// page. Without this it would vanish from the list and submit access could not
// be granted at all.
const GRANTABLE_PUBLIC_PAGES = ['/faq-platform-faq'];

// Every grantable page, alphabetical by name. Derived from the access model, so
// a new private page appears here automatically.
const PAGES = [
  ...Object.entries(PAGE_ACCESS)
    .filter(([, level]) => level === 'private')
    .map(([path]) => path),
  ...GRANTABLE_PUBLIC_PAGES,
]
  .map((path) => ({ path, label: labelFor(path) }))
  .sort((a, b) => a.label.localeCompare(b.label));

function formatDate(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return ''; }
}

// ---- One user's full controls (rendered inside the detail modal) -----------
function UserRow({ u, currentUserId, onChanged, onError, onStatus, onRequestConfirm }) {
  const [perms, setPerms] = useState(new Set(u.permissions || []));
  const [savingPerms, setSavingPerms] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetPw, setResetPw] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [editFullName, setEditFullName] = useState(u.full_name || '');
  const [editSalutation, setEditSalutation] = useState(u.salutation_name || '');
  const [savingNames, setSavingNames] = useState(false);

  useEffect(() => { setPerms(new Set(u.permissions || [])); }, [u.permissions]);
  useEffect(() => {
    setEditFullName(u.full_name || '');
    setEditSalutation(u.salutation_name || '');
  }, [u.full_name, u.salutation_name]);

  const saveNames = async (e) => {
    e.preventDefault();
    if (!editFullName.trim()) { onError('Full name is required.'); return; }
    setSavingNames(true);
    onError(''); onStatus('');
    try {
      const res = await fetch('/api/admin/update-user', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: u.id, full_name: editFullName, salutation_name: editSalutation }),
      });
      const data = await res.json();
      if (!res.ok) onError(data.error || 'Could not save the name.');
      else { onStatus(`Saved name for ${u.email}.`); onChanged(); }
    } catch { onError('Network error. Please try again.'); }
    finally { setSavingNames(false); }
  };

  const togglePerm = (path) => {
    setPerms((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  const savePerms = async () => {
    setSavingPerms(true);
    onError(''); onStatus('');
    try {
      const res = await fetch('/api/admin/set-permissions', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: u.id, permissions: [...perms] }),
      });
      const data = await res.json();
      if (!res.ok) onError(data.error || 'Could not save permissions.');
      else { onStatus(`Permissions saved for ${u.email}.`); onChanged(); }
    } catch { onError('Network error. Please try again.'); }
    finally { setSavingPerms(false); }
  };

  const toggleAdmin = () => {
    const makeAdmin = !u.is_admin;
    const apply = async () => {
      onError(''); onStatus('');
      try {
        const res = await fetch('/api/admin/set-admin', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: u.id, is_admin: makeAdmin }),
        });
        const data = await res.json();
        if (!res.ok) onError(data.error || 'Could not update administrator status.');
        else { onStatus(`${u.email} is ${makeAdmin ? 'now an administrator' : 'no longer an administrator'}.`); onChanged(); }
      } catch { onError('Network error. Please try again.'); }
    };
    if (makeAdmin) {
      onRequestConfirm({
        title: 'Grant administrator access?',
        message: `${u.email} will be able to manage all users, invitations, and permissions.`,
        confirmLabel: 'Make administrator',
        destructive: false,
        onConfirm: apply,
      });
    } else {
      apply();
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (resetPw.length < 12) { onError('New password must be at least 12 characters.'); return; }
    setResetBusy(true);
    onError(''); onStatus('');
    try {
      const res = await fetch('/api/admin/reset-password', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: u.id, new_password: resetPw }),
      });
      const data = await res.json();
      if (!res.ok) onError(data.error || 'Could not reset password.');
      else { onStatus(`Password reset for ${u.email}.`); setResetPw(''); setShowReset(false); }
    } catch { onError('Network error. Please try again.'); }
    finally { setResetBusy(false); }
  };

  const requestDelete = () => {
    onRequestConfirm({
      title: 'Delete this user?',
      message: `${u.email} and all their access will be permanently removed. This cannot be undone.`,
      confirmLabel: 'Delete user',
      destructive: true,
      onConfirm: async () => {
        onError(''); onStatus('');
        try {
          const res = await fetch(`/api/admin/delete-user/${u.id}`, { method: 'DELETE' });
          const data = await res.json();
          if (!res.ok) onError(data.error || 'Could not delete user.');
          else { onStatus(`Deleted ${u.email}.`); onChanged(); }
        } catch { onError('Network error. Please try again.'); }
      },
    });
  };

  const isSelf = u.id === currentUserId;

  return (
    <div className="admin-user-detail">
      <form onSubmit={saveNames} className="admin-name-form">
        <div className="auth-field">
          <label htmlFor={`full-${u.id}`}>Full name</label>
          <input id={`full-${u.id}`} type="text" value={editFullName} onChange={(e) => setEditFullName(e.target.value)} autoComplete="off" />
        </div>
        <div className="auth-field">
          <label htmlFor={`sal-${u.id}`}>Salutation name</label>
          <input id={`sal-${u.id}`} type="text" value={editSalutation} onChange={(e) => setEditSalutation(e.target.value)} autoComplete="off" />
        </div>
        <button type="submit" className="admin-btn-sm" disabled={savingNames}>
          {savingNames ? 'Saving…' : 'Save name'}
        </button>
      </form>

      <label className="admin-check">
        <input type="checkbox" checked={!!u.is_admin} onChange={toggleAdmin} disabled={isSelf} />
        <span>Administrator{isSelf ? ' (you)' : ''}</span>
      </label>

      <fieldset className="admin-perms">
        <legend>Pages {u.email} can access</legend>
        {u.is_admin ? (
          <p className="account-hint">Administrators can access every page.</p>
        ) : (
          <>
            {PAGES.map((p) => (
              <label key={p.path} className="admin-check">
                <input type="checkbox" checked={perms.has(p.path)} onChange={() => togglePerm(p.path)} />
                <span>{p.label}</span>
              </label>
            ))}
            <button type="button" className="admin-btn-sm" onClick={savePerms} disabled={savingPerms}>
              {savingPerms ? 'Saving…' : 'Save permissions'}
            </button>
          </>
        )}
      </fieldset>

      <div className="admin-user-actions">
        <button type="button" className="admin-btn-sm" onClick={() => setShowReset((s) => !s)} aria-expanded={showReset}>
          Reset password
        </button>
        <button
          type="button"
          className="admin-btn-sm admin-btn-danger"
          onClick={requestDelete}
          disabled={isSelf}
          aria-label={`Delete ${u.email}`}
        >
          Delete
        </button>
      </div>

      {showReset && (
        <form onSubmit={submitReset} className="admin-reset-form">
          <label htmlFor={`reset-${u.id}`}>New password for {u.email}</label>
          <input
            id={`reset-${u.id}`}
            type="text"
            value={resetPw}
            onChange={(e) => setResetPw(e.target.value)}
            minLength={12}
            autoComplete="off"
          />
          <button type="submit" className="admin-btn-sm" disabled={resetBusy}>
            {resetBusy ? 'Setting…' : 'Set password'}
          </button>
        </form>
      )}
    </div>
  );
}

// ---- The console -----------------------------------------------------------
function AdminConsole() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Invite form
  const [invEmail, setInvEmail] = useState('');
  const [invFullName, setInvFullName] = useState('');
  const [invSalutation, setInvSalutation] = useState('');
  const [invAdmin, setInvAdmin] = useState(false);
  const [invPerms, setInvPerms] = useState(new Set());
  const [invBody, setInvBody] = useState('');
  const [invBusy, setInvBusy] = useState(false);
  const defaultBodyRef = useRef('');

  useEffect(() => { document.title = 'Admin — users - Brookline Notepad'; }, []);

  // Redirect non-admins once auth has resolved (don't bounce during load).
  useEffect(() => {
    if (loading) return;
    if (!user || !user.is_admin) navigate('/');
  }, [user, loading, navigate]);

  const load = useCallback(async () => {
    try {
      const [uRes, iRes] = await Promise.all([
        fetch('/api/admin/users'),
        fetch('/api/admin/invitations'),
      ]);
      if (uRes.ok) setUsers(await uRes.json());
      if (iRes.ok) setInvites(await iRes.json());
    } catch {
      setError('Could not load users. Please refresh.');
    }
  }, []);

  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  // Load the default invitation body to pre-fill the editor.
  useEffect(() => {
    if (!user?.is_admin) return;
    fetch('/api/admin/invite-defaults')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d && typeof d.body === 'string') { defaultBodyRef.current = d.body; setInvBody(d.body); } })
      .catch(() => {});
  }, [user]);

  // Close the detail modal if its user disappears (e.g. after delete).
  useEffect(() => {
    if (selectedUserId && !users.find(u => u.id === selectedUserId)) setSelectedUserId(null);
  }, [users, selectedUserId]);

  if (!user || !user.is_admin) return null;

  const toggleInvPerm = (path) => {
    setInvPerms((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  const sendInvite = async (e) => {
    e.preventDefault();
    setError(''); setStatus('');
    setInvBusy(true);
    try {
      const res = await fetch('/api/admin/invite', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: invEmail,
          full_name: invFullName,
          salutation_name: invSalutation,
          is_admin: invAdmin,
          permissions: [...invPerms],
          body: invBody,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not send invitation.'); }
      else {
        setStatus(`Invitation sent to ${invEmail}.`);
        setInvEmail(''); setInvFullName(''); setInvSalutation(''); setInvAdmin(false); setInvPerms(new Set());
        setInvBody(defaultBodyRef.current);
        load();
      }
    } catch { setError('Network error. Please try again.'); }
    finally { setInvBusy(false); }
  };

  const revokeInvite = async (inv) => {
    setError(''); setStatus('');
    try {
      const res = await fetch(`/api/admin/invitations/${inv.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Could not revoke invitation.');
      else { setStatus(`Revoked invitation for ${inv.email}.`); load(); }
    } catch { setError('Network error. Please try again.'); }
  };

  // Filter + sort + cap the user list.
  const q = userSearch.trim().toLowerCase();
  const matched = users
    .filter(u => !q
      || (u.email && u.email.toLowerCase().includes(q))
      || (u.full_name && u.full_name.toLowerCase().includes(q))
      || (u.salutation_name && u.salutation_name.toLowerCase().includes(q)))
    .sort((a, b) => (a.email || '').localeCompare(b.email || ''));
  const shown = matched.slice(0, 10);
  const selectedUser = users.find(u => u.id === selectedUserId);

  return (
    <div className="auth-page">
      <a href="#main-content" className="auth-skip-link">Skip to main content</a>
      <Header title="Brookline Notepad" />

      <main id="main-content" tabIndex={-1} className="admin-content">
        <h2 className="account-page-title">Admin — users</h2>

        <div className="admin-live" role="status" aria-live="polite">{status}</div>
        {error && <div className="auth-error" role="alert">{error}</div>}

        {/* Invite */}
        <section className="account-section" aria-labelledby="adm-invite-h">
          <h3 id="adm-invite-h">Invite a user</h3>
          <p className="account-hint">
            They’ll get an email with a link to create an account.
          </p>
          <form onSubmit={sendInvite} className="auth-form" noValidate>
            <div className="auth-field">
              <label htmlFor="inv-fullname-f">Full name</label>
              <input id="inv-fullname-f" type="text" value={invFullName} onChange={(e) => setInvFullName(e.target.value)} autoComplete="off" />
            </div>
            <div className="auth-field">
              <label htmlFor="inv-salutation-f">Salutation name</label>
              <input id="inv-salutation-f" type="text" value={invSalutation} onChange={(e) => setInvSalutation(e.target.value)} autoComplete="off" />
            </div>
            <div className="auth-field">
              <label htmlFor="inv-email-f">Email</label>
              <input id="inv-email-f" type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} required autoComplete="off" inputMode="email" />
            </div>
            <div className="auth-field">
              <label htmlFor="inv-body-f">Invitation message</label>
              <p className="invite-greeting-preview">Hi {invSalutation.trim() || '[name]'},</p>
              <textarea
                id="inv-body-f"
                className="admin-textarea"
                value={invBody}
                onChange={(e) => setInvBody(e.target.value)}
                rows={9}
              />
            </div>
            <label className="admin-check">
              <input type="checkbox" checked={invAdmin} onChange={(e) => setInvAdmin(e.target.checked)} />
              <span>Make this user an administrator</span>
            </label>
            {!invAdmin && (
              <fieldset className="admin-perms">
                <legend>Grant access to</legend>
                {PAGES.map((p) => (
                  <label key={p.path} className="admin-check">
                    <input type="checkbox" checked={invPerms.has(p.path)} onChange={() => toggleInvPerm(p.path)} />
                    <span>{p.label}</span>
                  </label>
                ))}
              </fieldset>
            )}
            <button type="submit" className="auth-btn" disabled={invBusy}>
              {invBusy ? 'Sending…' : 'Send invitation'}
            </button>
          </form>
        </section>

        {/* Pending invitations */}
        {invites.length > 0 && (
          <section className="account-section" aria-labelledby="adm-pending-h">
            <h3 id="adm-pending-h">Pending invitations</h3>
            <ul className="admin-invite-list">
              {invites.map((inv) => (
                <li key={inv.id} className="admin-invite-item">
                  <div>
                    <span className="admin-user-name">{inv.full_name || inv.salutation_name || '—'}</span>
                    <span className="admin-user-email">{inv.email}</span>
                    <span className="passkey-meta">
                      {inv.is_admin ? 'Administrator · ' : ''}Expires {formatDate(inv.expires_at)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="admin-btn-sm admin-btn-danger"
                    onClick={() => revokeInvite(inv)}
                    aria-label={`Revoke invitation for ${inv.email}`}
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Users */}
        <section className="account-section" aria-labelledby="adm-users-h">
          <h3 id="adm-users-h">Users ({users.length})</h3>
          <div className="auth-field">
            <label htmlFor="user-search">Search users</label>
            <input
              id="user-search"
              type="search"
              className="admin-search"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Filter by name or email"
              autoComplete="off"
            />
          </div>
          {shown.length === 0 ? (
            <p className="account-hint">No matching users.</p>
          ) : (
            <ul className="admin-user-compact-list">
              {shown.map((u) => (
                <li key={u.id}>
                  <button type="button" className="admin-user-compact" onClick={() => setSelectedUserId(u.id)}>
                    <span className="admin-user-name">{u.full_name || u.salutation_name || '—'}</span>
                    <span className="admin-user-email">{u.email}</span>
                    {u.is_admin && <span className="admin-badge">Administrator</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {matched.length > 10 && (
            <p className="account-hint">Showing the first 10 of {matched.length} matches. Refine your search to narrow it down.</p>
          )}
        </section>

        <button className="auth-link-btn" onClick={() => navigate('/')}>← Back to Home</button>
      </main>

      <Modal
        open={!!selectedUser}
        onClose={() => setSelectedUserId(null)}
        title={selectedUser ? ((selectedUser.full_name || selectedUser.salutation_name) ? `${selectedUser.full_name || selectedUser.salutation_name} — ${selectedUser.email}` : selectedUser.email) : ''}
      >
        {selectedUser && (
          <>
            <div className="admin-live" role="status" aria-live="polite">{status}</div>
            {error && <div className="auth-error" role="alert">{error}</div>}
            <UserRow
              u={selectedUser}
              currentUserId={user.id}
              onChanged={load}
              onError={setError}
              onStatus={setStatus}
              onRequestConfirm={setConfirm}
            />
          </>
        )}
      </Modal>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        destructive={confirm?.destructive}
        onConfirm={() => { const c = confirm; setConfirm(null); c?.onConfirm?.(); }}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

export default AdminConsole;
